import React, { useState } from 'react';
import {
  Box, Button, IconButton, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useEditMode } from './EditModeContext';

const EMPTY_UBO_FORM = { name: '', role: '', linkedin: '' };
const EMPTY_ENTITY_FORM = {
  legal_name: '', state_code: '', state_id: '', status: '',
  incorporation_date: '', registered_agent_name: '', registered_agent_address: '',
  oc_url: '',
};

export const AddUBOButton = ({ ubos, graphData, onUpdateUbos, onUpdateGraph, onCombinedUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_UBO_FORM);

  if (!isEditMode) return null;

  const handleSubmit = () => {
    const { name, role, linkedin } = form;
    if (!name.trim() || !role.trim()) return;

    const nodeId = crypto.randomUUID();
    const nameParts = name.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const newUbo = {
      name: name.trim(), role: role.trim(), online_references_count: 0,
      cited_on_website: false, affiliated_addresses: [], sources: ['SOS'],
      linkedin: linkedin.trim() || null, associated_entities: [], node_id: nodeId,
    };

    const personNodeId = `person_${nodeId}`;
    const newNode = {
      id: personNodeId, label: name.trim(), type: 'PERSON',
      data: { first_name: firstName, last_name: lastName, linkedin: linkedin.trim() || null, role: role.trim() },
    };

    if (onCombinedUpdate) {
      onCombinedUpdate(prev => {
        const gd = prev.graph_data || {};
        const mainEntityNode = (gd.nodes || []).find(n => n.type === 'COMPANY');
        const mainEntityId = mainEntityNode?.id || 'main_entity';
        const newEdge = { label: role.trim(), type: 'associated_with', source: personNodeId, target: mainEntityId, confidence: 100.0 };
        return {
          ubos: [...(prev.ubos || []), newUbo],
          graph_data: { ...gd, nodes: [...(gd.nodes || []), newNode], edges: [...(gd.edges || []), newEdge] },
        };
      });
    } else {
      onUpdateUbos([...ubos, newUbo]);
      const mainEntityNode = graphData?.nodes?.find(n => n.type === 'COMPANY');
      const mainEntityId = mainEntityNode?.id || 'main_entity';
      const newEdge = { label: role.trim(), type: 'associated_with', source: personNodeId, target: mainEntityId, confidence: 100.0 };
      onUpdateGraph({ ...graphData, nodes: [...(graphData?.nodes || []), newNode], edges: [...(graphData?.edges || []), newEdge] });
    }

    addChangeDescription(`Added UBO: ${name.trim()}`);
    setForm(EMPTY_UBO_FORM);
    setOpen(false);
  };

  const setField = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <>
      <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={() => setOpen(true)} sx={{ borderColor: '#4566EB', color: '#4566EB' }}>
        Add UBO
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add UBO</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Name" value={form.name} onChange={setField('name')} fullWidth required />
          <TextField label="Role" value={form.role} onChange={setField('role')} fullWidth required />
          <TextField label="LinkedIn URL" value={form.linkedin} onChange={setField('linkedin')} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!form.name.trim() || !form.role.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const AddEntityButton = ({ ubo, ubos, graphData, onUpdateUbos, onUpdateGraph, onCombinedUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_ENTITY_FORM);

  if (!isEditMode) return null;

  const handleSubmit = () => {
    if (!form.legal_name.trim()) return;
    const entityId = crypto.randomUUID();
    const newEntity = {
      business_entity_id: entityId, legal_name: form.legal_name.trim(),
      state_code: form.state_code.trim().toUpperCase(), state_id: form.state_id.trim(),
      status: form.status.trim(), incorporation_date: form.incorporation_date || null,
      registered_agent: { name: form.registered_agent_name.trim() || null, address: form.registered_agent_address.trim() || null },
      oc_url: form.oc_url.trim() || null,
    };
    const companyNodeId = `company_${entityId}`;
    const personNodeId = `person_${ubo.node_id}`;
    const newNode = { id: companyNodeId, label: form.legal_name.trim(), type: 'COMPANY', data: { ...newEntity } };
    const newEdge = { label: ubo.role || 'associated_with', type: 'associated_with', source: personNodeId, target: companyNodeId, confidence: 100.0 };

    if (onCombinedUpdate) {
      onCombinedUpdate(prev => {
        const updatedUbos = (prev.ubos || []).map(u => {
          if (u.node_id !== ubo.node_id) return u;
          return { ...u, associated_entities: [...(u.associated_entities || []), newEntity] };
        });
        const gd = prev.graph_data || {};
        return {
          ubos: updatedUbos,
          graph_data: { ...gd, nodes: [...(gd.nodes || []), newNode], edges: [...(gd.edges || []), newEdge] },
        };
      });
    } else {
      const updatedUbos = ubos.map(u => {
        if (u.node_id !== ubo.node_id) return u;
        return { ...u, associated_entities: [...(u.associated_entities || []), newEntity] };
      });
      onUpdateUbos(updatedUbos);
      onUpdateGraph({ ...graphData, nodes: [...(graphData?.nodes || []), newNode], edges: [...(graphData?.edges || []), newEdge] });
    }

    addChangeDescription(`Added entity "${form.legal_name.trim()}" to ${ubo.name}`);
    setForm(EMPTY_ENTITY_FORM);
    setOpen(false);
  };

  const setField = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <>
      <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={() => setOpen(true)} sx={{ borderColor: '#4566EB', color: '#4566EB', mt: 0.5 }}>
        Add entity
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add entity for {ubo.name}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Legal Name" value={form.legal_name} onChange={setField('legal_name')} fullWidth required placeholder="e.g. Acme Corp LLC" />
          <TextField label="State Code" value={form.state_code} onChange={setField('state_code')} fullWidth inputProps={{ maxLength: 2 }} placeholder="e.g. NY" />
          <TextField label="Company Number" value={form.state_id} onChange={setField('state_id')} fullWidth placeholder="e.g. 6439824" />
          <TextField label="Status" value={form.status} onChange={setField('status')} fullWidth placeholder="e.g. Active" />
          <TextField label="Incorporation Date" value={form.incorporation_date} onChange={setField('incorporation_date')} fullWidth placeholder="YYYY-MM-DD" />
          <TextField label="Registered Agent Name" value={form.registered_agent_name} onChange={setField('registered_agent_name')} fullWidth placeholder="e.g. John Smith" />
          <TextField label="Registered Agent Address" value={form.registered_agent_address} onChange={setField('registered_agent_address')} fullWidth placeholder="e.g. 123 Main St, New York, NY 10001" />
          <TextField label="OpenCorporates URL" value={form.oc_url} onChange={setField('oc_url')} fullWidth placeholder="e.g. https://opencorporates.com/companies/us_ny/6439824" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!form.legal_name.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const RemoveEntityButton = ({ entityId, ubo, ubos, graphData, onUpdateUbos, onUpdateGraph, onCombinedUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  if (!isEditMode) return null;

  const handleRemove = (e) => {
    e.stopPropagation();
    const entity = (ubo.associated_entities || []).find(ent => ent.business_entity_id === entityId);
    const entityName = entity?.legal_name || entityId;
    const companyNodeId = `company_${entityId}`;

    if (onCombinedUpdate) {
      onCombinedUpdate(prev => {
        const updatedUbos = (prev.ubos || []).map(u => {
          if (u.node_id !== ubo.node_id) return u;
          return { ...u, associated_entities: (u.associated_entities || []).filter(ent => ent.business_entity_id !== entityId) };
        });
        const gd = prev.graph_data || {};
        return {
          ubos: updatedUbos,
          graph_data: {
            ...gd,
            nodes: (gd.nodes || []).filter(n => n.id !== companyNodeId && n.id !== entityId),
            edges: (gd.edges || []).filter(e => {
              const s = String(e.source?.id || e.source);
              const t = String(e.target?.id || e.target);
              return s !== companyNodeId && t !== companyNodeId && s !== entityId && t !== entityId;
            }),
          },
        };
      });
    } else {
      const updatedUbos = ubos.map(u => {
        if (u.node_id !== ubo.node_id) return u;
        return { ...u, associated_entities: (u.associated_entities || []).filter(ent => ent.business_entity_id !== entityId) };
      });
      onUpdateUbos(updatedUbos);
      onUpdateGraph({
        ...graphData,
        nodes: (graphData?.nodes || []).filter(n => n.id !== companyNodeId && n.id !== entityId),
        edges: (graphData?.edges || []).filter(e => {
          const s = String(e.source?.id || e.source);
          const t = String(e.target?.id || e.target);
          return s !== companyNodeId && t !== companyNodeId && s !== entityId && t !== entityId;
        }),
      });
    }

    addChangeDescription(`Removed entity "${entityName}" from ${ubo.name}`);
  };

  return (
    <IconButton size="small" onClick={handleRemove} sx={{ color: '#d32f2f', p: 0.25 }}>
      <DeleteIcon fontSize="small" />
    </IconButton>
  );
};

export const RemoveUBOButton = ({ ubo, ubos, graphData, onUpdateUbos, onUpdateGraph, onCombinedUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  if (!isEditMode) return null;

  const handleRemove = (e) => {
    e.stopPropagation();
    const personNodeId = `person_${ubo.node_id}`;

    if (onCombinedUpdate) {
      onCombinedUpdate(prev => {
        const gd = prev.graph_data || {};
        return {
          ubos: (prev.ubos || []).filter(u => u.node_id !== ubo.node_id),
          graph_data: {
            ...gd,
            nodes: (gd.nodes || []).filter(n => n.id !== personNodeId),
            edges: (gd.edges || []).filter(e => {
              const s = String(e.source?.id || e.source);
              const t = String(e.target?.id || e.target);
              return s !== personNodeId && t !== personNodeId;
            }),
          },
        };
      });
    } else {
      onUpdateUbos(ubos.filter(u => u.node_id !== ubo.node_id));
      onUpdateGraph({
        ...graphData,
        nodes: (graphData?.nodes || []).filter(n => n.id !== personNodeId),
        edges: (graphData?.edges || []).filter(e => e.source !== personNodeId && e.target !== personNodeId),
      });
    }

    addChangeDescription(`Removed UBO: ${ubo.name}`);
  };

  return (
    <IconButton size="small" onClick={handleRemove} sx={{ color: '#d32f2f', p: 0.25 }}>
      <DeleteIcon fontSize="small" />
    </IconButton>
  );
};

export const EditUBOButton = ({ ubo, ubos, graphData, onUpdateUbos, onUpdateGraph, onCombinedUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', role: '', address: '' });

  if (!isEditMode) return null;

  const handleOpen = () => {
    setForm({ name: ubo.name || '', role: ubo.role || '', address: (ubo.affiliated_addresses && ubo.affiliated_addresses[0]) || '' });
    setDialogOpen(true);
  };

  const handleSave = () => {
    const personId = `person_${ubo.node_id}`;
    const originalName = ubo.name;
    const parts = form.name.trim().split(' ');

    const matchesPersonNode = (n) =>
      n.id === personId || n.id === ubo.node_id ||
      (n.type === 'PERSON' && n.label?.toUpperCase() === originalName?.toUpperCase());

    if (onCombinedUpdate) {
      onCombinedUpdate(prev => {
        const updatedUbos = (prev.ubos || []).map(u => {
          if (u.node_id !== ubo.node_id) return u;
          return { ...u, name: form.name, role: form.role, affiliated_addresses: form.address.trim() ? [form.address.trim()] : [] };
        });
        const gd = prev.graph_data || {};
        // Find the matching node ID for edge updates
        const matchedNode = (gd.nodes || []).find(n => matchesPersonNode(n));
        const matchedNodeId = matchedNode?.id;

        const updatedNodes = (gd.nodes || []).map(n => {
          if (!matchesPersonNode(n)) return n;
          return { ...n, label: form.name, data: { ...n.data, first_name: parts[0] || '', last_name: parts.slice(1).join(' ') || '', role: form.role } };
        });

        // Also update edge labels from this person if role changed
        const updatedEdges = matchedNodeId ? (gd.edges || []).map(e => {
          const s = String(e.source?.id || e.source);
          if (s !== matchedNodeId) return e;
          return { ...e, label: form.role };
        }) : (gd.edges || []);

        return { ubos: updatedUbos, graph_data: { ...gd, nodes: updatedNodes, edges: updatedEdges } };
      });
    } else {
      const updatedUbos = ubos.map(u => {
        if (u.node_id !== ubo.node_id) return u;
        return { ...u, name: form.name, role: form.role, affiliated_addresses: form.address.trim() ? [form.address.trim()] : [] };
      });
      onUpdateUbos(updatedUbos);
    }

    addChangeDescription(`Edited UBO: ${form.name}`);
    setDialogOpen(false);
  };

  return (
    <>
      <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={handleOpen} sx={{ borderColor: '#4566EB', color: '#4566EB' }}>
        Edit
      </Button>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit UBO: {ubo.name}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField label="Name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} size="small" />
          <TextField label="Role" value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))} size="small" placeholder="e.g. owner, director" />
          <TextField label="Affiliated address" value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} size="small" placeholder="Full address" multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const EditLinkedInButton = ({ ubo, ubos, graphData, onUpdateUbos, onUpdateGraph, onCombinedUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [linkedinUrl, setLinkedinUrl] = useState('');

  if (!isEditMode) return null;

  const handleSave = () => {
    if (!linkedinUrl.trim()) return;
    const personId = `person_${ubo.node_id}`;

    if (onCombinedUpdate) {
      onCombinedUpdate(prev => {
        const updatedUbos = (prev.ubos || []).map(u => {
          if (u.node_id !== ubo.node_id) return u;
          return { ...u, linkedin: linkedinUrl.trim() };
        });
        const gd = prev.graph_data || {};
        const updatedNodes = (gd.nodes || []).map(n => {
          if (n.id !== personId) return n;
          return { ...n, data: { ...n.data, linkedin: linkedinUrl.trim() } };
        });
        return { ubos: updatedUbos, graph_data: { ...gd, nodes: updatedNodes } };
      });
    } else {
      const updatedUbos = ubos.map(u => {
        if (u.node_id !== ubo.node_id) return u;
        return { ...u, linkedin: linkedinUrl.trim() };
      });
      onUpdateUbos(updatedUbos);
      if (graphData && onUpdateGraph) {
        const updatedNodes = graphData.nodes.map(n => {
          if (n.id !== personId) return n;
          return { ...n, data: { ...n.data, linkedin: linkedinUrl.trim() } };
        });
        onUpdateGraph({ ...graphData, nodes: updatedNodes });
      }
    }

    addChangeDescription(`Added LinkedIn for ${ubo.name}: ${linkedinUrl.trim()}`);
    setDialogOpen(false);
    setLinkedinUrl('');
  };

  return (
    <>
      <Button size="small" variant="outlined" startIcon={<LinkedInIcon />}
        onClick={() => { setLinkedinUrl(ubo.linkedin || ''); setDialogOpen(true); }}
        sx={{ borderColor: '#0077b5', color: '#0077b5' }}
      >
        {ubo.linkedin ? 'Edit LinkedIn' : 'Add LinkedIn'}
      </Button>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{ubo.linkedin ? 'Edit' : 'Add'} LinkedIn for {ubo.name}</DialogTitle>
        <DialogContent>
          <TextField label="LinkedIn URL" placeholder="https://www.linkedin.com/in/..." value={linkedinUrl}
            onChange={(e) => setLinkedinUrl(e.target.value)} fullWidth sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!linkedinUrl.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
